import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import {
  GetMasterRecordAsOf,
  GetMasterRecordHistory,
  GetMasterRecordVersion,
} from "../../Service/_index";
import { useMasterHistoryAccess } from "./useMasterHistoryAccess";
import {
  DialogHeader,
  FormattedValue,
  HistoryEmpty,
  HistoryError,
  HistorySkeleton,
  Note,
  SummaryStrip,
  navySwitchSx,
  outlinedButtonSx,
  switchLabelSx,
} from "./HistoryParts";
import {
  flattenSnapshot,
  formatDateTime,
  formatDay,
  humanizeField,
  sameValue,
} from "./historyFormat";

const headCellSx = {
  backgroundColor: "#F9FAFB",
  color: "#475467",
  fontSize: "12px",
  fontWeight: 600,
  borderBottom: "1px solid #eaecf0",
};

const bodyCellSx = { fontSize: "12.5px", color: "#101828", verticalAlign: "top" };

const describeBasis = (result, day) => {
  const { entry, basis } = result;
  if (basis === "notYetCreated") {
    return { tone: "warn", text: `This record had not been created yet on ${day}.` };
  }
  if (!result.found) {
    return {
      tone: "warn",
      text: `No change history is recorded for this record, so its values on ${day} are not available.`,
    };
  }
  if (result.deleted) {
    return {
      tone: "warn",
      text: `This record had already been deleted by ${day} (${formatDateTime(entry.changedAt)}). Showing its last values.`,
    };
  }
  if (result.beforeTracking) {
    return {
      tone: "warn",
      text: `Change tracking only started on ${formatDateTime(entry.changedAt)}, after ${day}. Showing the earliest recorded state, which may not be exactly what it was then.`,
    };
  }
  if (basis === "trimmed") {
    return {
      tone: "warn",
      text: `Older versions were removed to keep history at a manageable size. Showing the earliest version still stored (v${entry.version}, ${formatDateTime(entry.changedAt)}); it may differ from ${day}.`,
    };
  }
  return {
    tone: "info",
    text: `Showing v${entry.version}, the latest change made on or before ${day} (${formatDateTime(entry.changedAt)}).`,
  };
};

/** Field | On {date} | Now, so a policy's saved vehicle can be compared with today's master. */
const MasterAsOfDialog = ({ open, onClose, entityType, entityId, asOf, onViewHistory }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { entities } = useMasterHistoryAccess();

  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState({ status: "loading" });
  const [onlyChanged, setOnlyChanged] = useState(true);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setView({ status: "loading" });
    setOnlyChanged(true);
    const finish = (next) => {
      if (!cancelled) setView(next);
    };

    (async () => {
      const [then, history] = await Promise.all([
        GetMasterRecordAsOf(entityType, entityId, asOf),
        GetMasterRecordHistory(entityType, entityId),
      ]);
      const failed = [then, history].find((res) => res.error);
      if (failed) return finish({ status: "error", message: failed.message });
      if (then.enabled === false || history.enabled === false) return finish({ status: "disabled" });

      let now = null;
      if (then.found && history.currentVersion) {
        if (then.entry.version === history.currentVersion) now = then.entry;
        else {
          const latest = await GetMasterRecordVersion(entityType, entityId, history.currentVersion);
          if (latest.error || !latest.data) {
            return finish({
              status: "error",
              message: latest.message || "Could not load the current record.",
            });
          }
          now = latest.data;
        }
      }
      return finish({
        status: "ready",
        then,
        now,
        currentVersion: history.currentVersion,
        recordLabel: history.recordLabel,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [open, entityType, entityId, asOf, attempt]);

  const rows = useMemo(() => {
    if (view.status !== "ready" || !view.then.found || !view.now) return [];
    const before = flattenSnapshot(view.then.entry.snapshot);
    const after = flattenSnapshot(view.now.snapshot);
    const fields = [...Object.keys(before), ...Object.keys(after).filter((f) => !(f in before))];
    const all = fields.map((field) => ({
      field,
      before: before[field],
      after: after[field],
      changed: !sameValue(before[field], after[field]),
    }));
    return [...all.filter((row) => row.changed), ...all.filter((row) => !row.changed)];
  }, [view]);

  const day = formatDay(asOf);
  const changedCount = rows.filter((row) => row.changed).length;
  const shownRows = onlyChanged ? rows.filter((row) => row.changed) : rows;
  const masterLabel =
    entities.find((item) => item.entityType === entityType)?.label || humanizeField(entityType);
  const basis = view.status === "ready" ? describeBasis(view.then, day) : null;
  const deletedSince = view.status === "ready" && view.now?.action === "deleted";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="master-asof-title"
    >
      <DialogHeader
        titleId="master-asof-title"
        icon={<EventNoteIcon sx={{ fontSize: 20 }} />}
        title={`${masterLabel} as on ${day}`}
        subtitle={
          (view.status === "ready" && view.recordLabel) || "How this master looked when the policy was created"
        }
        onClose={onClose}
        actions={
          view.status === "ready" && view.then.found ? (
            <FormControlLabel
              sx={switchLabelSx}
              control={
                <Switch
                  size="small"
                  sx={navySwitchSx}
                  checked={onlyChanged}
                  onChange={(event) => setOnlyChanged(event.target.checked)}
                />
              }
              label="Only changed fields"
            />
          ) : null
        }
      />

      <Box
        sx={{
          p: 2.5,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          maxHeight: fullScreen ? "none" : "70vh",
        }}
      >
        {view.status === "loading" && <HistorySkeleton rows={2} />}

        {view.status === "error" && (
          <HistoryError message={view.message} onRetry={() => setAttempt((n) => n + 1)} />
        )}

        {view.status === "disabled" && (
          <HistoryEmpty
            icon={<HistoryToggleOffIcon />}
            title="History tracking is turned off"
            text="Changes to master data are not being recorded right now."
          />
        )}

        {view.status === "ready" && (
          <>
            <Note tone={basis.tone}>{basis.text}</Note>
            {deletedSince && (
              <Note tone="warn">
                This record has since been deleted. The Now column shows its last values.
              </Note>
            )}

            {view.then.found && (
              <>
                <SummaryStrip
                  items={[
                    { label: "Version then", value: `v${view.then.entry.version}` },
                    { label: "Version now", value: `v${view.currentVersion}` },
                    { label: "Fields changed since", value: changedCount, color: changedCount ? "#B54708" : undefined },
                  ]}
                />

                {shownRows.length ? (
                  <TableContainer sx={{ border: "1px solid #eaecf0", borderRadius: "8px" }}>
                    <Table size="small" sx={{ minWidth: 480 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headCellSx}>Field</TableCell>
                          <TableCell sx={headCellSx}>On {day}</TableCell>
                          <TableCell sx={headCellSx}>Now</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shownRows.map((row) => (
                          <TableRow
                            key={row.field}
                            sx={{ backgroundColor: row.changed ? "#FFFAEB" : "transparent" }}
                          >
                            <TableCell
                              sx={{
                                ...bodyCellSx,
                                fontWeight: 500,
                                color: "#344054",
                                borderLeft: `3px solid ${row.changed ? "#F79009" : "transparent"}`,
                              }}
                            >
                              {humanizeField(row.field)}
                            </TableCell>
                            <TableCell sx={bodyCellSx}>
                              <FormattedValue field={row.field} value={row.before} />
                            </TableCell>
                            <TableCell sx={{ ...bodyCellSx, fontWeight: row.changed ? 600 : 400 }}>
                              <FormattedValue field={row.field} value={row.after} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <HistoryEmpty
                    icon={<CheckCircleOutlineIcon />}
                    title="Nothing has changed"
                    text={`Every value is the same as it was on ${day}. Turn off "Only changed fields" to see them all.`}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          px: 2.25,
          py: 1.25,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid #eaecf0",
          flexShrink: 0,
        }}
      >
        {onViewHistory && (
          <Button variant="outlined" size="small" onClick={onViewHistory} sx={outlinedButtonSx}>
            View full history
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            borderRadius: "8px",
            backgroundColor: "#1E3A5F",
            "&:hover": { backgroundColor: "#16304E" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default MasterAsOfDialog;
